import { FILTER_KEY_PREFIX } from '../constants/filter-tree.js';
import { getCustomerId } from './index.js';
import { buildShortLongMaps } from './shorten-url.js';
import { isCollectionPage } from './validate.js';

export let shortParamsMap = new Map();
export let longParamsMap = new Map();

const DEFAULT_SEPARATOR = ',';
const REGEX_ESCAPE_PATTERN = /[.*+?^${}()|[\]\\]/g;

/**
 * @param { Array } shortenUrlParamList - List of ['filter_option_id:short_id']
 * @type {import('./index.d').initShortenMaps}
 */
export function initShortenMaps({ shortenUrlParamList } = {}) {
  const list = Array.isArray(shortenUrlParamList) ? shortenUrlParamList : [];
  const { shortParamsMap: sMap, longParamsMap: lMap } = buildShortLongMaps(list);
  shortParamsMap = sMap;
  longParamsMap = lMap;
}

/**
 * @param url - The URL object to modify.
 * @param key - The query parameter key.
 * @param value - The query parameter value (or an array of values).
 * @param options - Options to force appending the value.
 * @type {import('./index.d').applyParamOperation}
 */
function applyParamOperation(url, key, value, { forceAppend = false } = {}) {
  if (Array.isArray(value)) {
    value.forEach((v) => url.searchParams.append(key, v));
  } else {
    if (forceAppend) {
      url.searchParams.append(key, value);
    } else {
      url.searchParams.set(key, value);
    }
  }
}

/**
 * @type {import('./index.d').setSessionStorage}
 */
function setSessionStorage(key, value, action = 'add') {
  let data = JSON.parse(sessionStorage.getItem(key) || '[]');

  if (action === 'add') {
    if (!data.includes(value)) {
      data.push(value);
    }
  } else if (action === 'delete') {
    data = data.filter((item) => item !== value);
  }

  sessionStorage.setItem(key, JSON.stringify(data));
}

/**
 * @type {import('./index.d').getSessionStorage}
 */
function getSessionStorage(key) {
  const data = sessionStorage.getItem(key);
  return data ? JSON.parse(data) : [];
}

/**
 * Gets the configured separator for multiple values in a URL parameter.
 * Defaults to ',' if not specified.
 * @returns {string} The separator character.
 */
function getMultipleValueSeparator() {
  const separator =
    window.boostWidgetIntegration?.config?.customization?.filter?.customizedAPIParams
      ?.multipleValueSeparator;
  return typeof separator === 'string' && separator.length > 0 ? separator : DEFAULT_SEPARATOR;
}

/**
 * @type {import('./index.d').setShortenedParam}
 */
function setShortenedParam(url, key, rawValues) {
  const prefix = url.search.startsWith('?') ? url.search.slice(1) : '';
  const pairs = prefix ? prefix.split('&') : [];
  const separator = getMultipleValueSeparator();
  const encodedValues = rawValues
    .map((v) => encodeURIComponent(v).replace(/%20/g, '+'))
    .join(separator);

  const filtered = pairs.filter((p) => !p.startsWith(encodeURIComponent(key) + '='));
  const all = [...filtered, `${encodeURIComponent(key)}=${encodedValues}`];
  all.sort((a, b) => {
    const [rawKeyA] = a.split('=');
    const [rawKeyB] = b.split('=');
    return decodeURIComponent(rawKeyA).localeCompare(decodeURIComponent(rawKeyB));
  });
  url.search = all.length ? `?${all.join('&')}` : '';
}

/**
 * @type {import('./index.d').removeShortenedParam}
 */
function removeShortenedParam(url, key) {
  const prefix = url.search.startsWith('?') ? url.search.slice(1) : '';
  const pairs = prefix ? prefix.split('&') : [];
  const encKey = encodeURIComponent(key);
  const filtered = pairs.filter((p) => !p.startsWith(encKey + '='));

  url.search = filtered.length ? `?${filtered.join('&')}` : '';
}

/**
 * @param { string } behavior - push | replace | null - default behavior = push
 * @param { string } urlHref - current URL to push history
 * @param { Object } state - save state or empty object
 * @type {import('./index.d').mutateHistory}
 */
function mutateHistory(behavior = 'push', urlHref, state = {}) {
  if (behavior === 'push') {
    // not prevent then create a new history
    window.history.pushState(state, '', urlHref);
  } else {
    // replace history to avoid create new history
    window.history.replaceState(state, '', urlHref);
  }
}

/**
 * @type {import('./index.d').convertValueRequestStockStatus}
 */
export const convertValueRequestStockStatus = (v) => {
  if (Array.isArray(v)) {
    return v.map((_v) => {
      return _v !== 'out-of-stock';
    });
  }

  return v !== 'out-of-stock';
};

/**
 * @type {import('./index.d').appendQueryParamHistory}
 */

export function appendQueryParamHistory(
  key,
  value,
  replace = false,
  { preventPushHistory = false, isShortenUrlParam = false, urlScheme = 2 } = {}
) {
  if (isShortenUrlParam && urlScheme !== 0 && shortParamsMap.has(key)) {
    key = shortParamsMap.get(key);
  }

  const url = new URL(window.location.href);
  const { searchParams } = url;

  if (!isShortenUrlParam) {
    applyParamOperation(url, key, value, { forceAppend: true });
  } else {
    if (urlScheme === 0 || urlScheme === 1) {
      applyParamOperation(url, key, value, { forceAppend: true });
    } else if (urlScheme === 2) {
      if (!searchParams.has(key)) {
        sessionStorage.removeItem(key);
      }
      setSessionStorage(key, value, 'add');
      const arr = getSessionStorage(key);
      setShortenedParam(url, key, arr);
    }
  }

  if (!isShortenUrlParam || (isShortenUrlParam && urlScheme !== 2)) {
    url.searchParams.sort();
  }

  if (replace) {
    window.history.replaceState({}, '', url.href);
  } else if (!preventPushHistory) {
    window.history.pushState({}, '', url.href);
  }
}

/**
 * @param {string} key - param key (long form, ex: 'pf_opt_color')
 * @param {string|string[]} value - the value(s). If array, we handle it by scheme
 * @param {boolean} replace - if true => use history.replaceState instead of push
 * @param {object} config
 *   preventPushHistory  => skip calling pushState if true
 *   isShortenUrlParam   => if true, map key => shortKey
 *   urlScheme           => 0,1,2 (0=old,1=append,2=combine)
 * @type {import('./index.d').setQueryParamHistory}
 */
export function setQueryParamHistory(
  key,
  value,
  replace = false,
  { preventPushHistory = false, isShortenUrlParam = false, urlScheme = 2 } = {}
) {
  if (isShortenUrlParam && urlScheme !== 0 && shortParamsMap.has(key)) {
    key = shortParamsMap.get(key);
  }

  const url = new URL(window.location.href);
  const { searchParams } = url;

  if (!isShortenUrlParam) {
    applyParamOperation(url, key, value);
  } else {
    if (urlScheme === 0) {
      applyParamOperation(url, key, value);
    } else if (urlScheme === 1) {
      url.searchParams.delete(key);
      applyParamOperation(url, key, value, { forceAppend: true });
    } else if (urlScheme === 2) {
      if (!searchParams.has(key)) {
        sessionStorage.removeItem(key);
      }
      const list = Array.isArray(value) ? value : [value];
      sessionStorage.setItem(key, JSON.stringify(list));
      setShortenedParam(url, key, list);
    }
  }

  if (!isShortenUrlParam || (isShortenUrlParam && urlScheme !== 2)) {
    url.searchParams.sort();
  }

  if (replace) {
    window.history.replaceState({}, '', url.href);
    return;
  }
  if (!preventPushHistory) {
    window.history.pushState({}, '', url.href);
  }
}

/**
 * @type {import('./index.d').removeQueryParamHistory}
 */
export const removeQueryParamHistory = (
  key,
  value,
  { isShortenUrlParam = false, urlScheme = 2, preventPushHistory = false } = {}
) => {
  removeQueryParamsHistory([{ key, value }], { isShortenUrlParam, urlScheme, preventPushHistory });
};

/**
 * @type {import('./index.d').removeQueryParamsHistory}
 */
export function removeQueryParamsHistory(
  params,
  { isShortenUrlParam = false, urlScheme = 2, preventPushHistory = false } = {}
) {
  const url = new URL(window.location.href);
  const { searchParams } = url;

  params.forEach(({ key, value }) => {
    if (isShortenUrlParam && urlScheme !== 0 && shortParamsMap.has(key)) {
      key = shortParamsMap.get(key);
    }

    if (!searchParams.has(key)) return;

    if (isShortenUrlParam && urlScheme === 2) {
      setSessionStorage(key, value, 'delete');
      const list = getSessionStorage(key);
      if (list.length) {
        setShortenedParam(url, key, list);
      } else {
        sessionStorage.removeItem(key);
        removeShortenedParam(url, key);
      }
    } else {
      const oldVals = searchParams.getAll(key).filter((v) => v !== value);
      searchParams.delete(key);
      oldVals.forEach((v) => searchParams.append(key, v));
    }
  });

  if (!isShortenUrlParam || (isShortenUrlParam && urlScheme !== 2)) {
    url.searchParams.sort();
  }

  const behavior = preventPushHistory ? 'replace' : 'push';
  mutateHistory(behavior, url.href, {});
}

/**
 * @type {import('./index.d').deleteQueryParamHistory}
 */
export const deleteQueryParamHistory = (
  key,
  { preventPushHistory = false, isShortenUrlParam = false, urlScheme = 2 } = {}
) => {
  deleteQueryParamsHistory([key], { preventPushHistory, isShortenUrlParam, urlScheme });
};

/**
 * @type {import('./index.d').deleteQueryParamsHistory}
 */
export const deleteQueryParamsHistory = (
  keys,
  { preventPushHistory = false, isShortenUrlParam = false, urlScheme = 2 } = {}
) => {
  const url = new URL(window.location.href);

  keys.forEach((fullKey) => {
    const key =
      isShortenUrlParam && urlScheme !== 0 && shortParamsMap.has(fullKey)
        ? shortParamsMap.get(fullKey)
        : fullKey;

    sessionStorage.removeItem(fullKey);

    if (isShortenUrlParam && urlScheme === 2) {
      removeShortenedParam(url, key);
    } else {
      url.searchParams.delete(key);
    }
  });

  if (!isShortenUrlParam || (isShortenUrlParam && urlScheme !== 2)) {
    url.searchParams.sort();
  }

  const behavior = preventPushHistory ? 'replace' : 'push';
  mutateHistory(behavior, url.href, {});
};

/**
 * @type {import('./index.d').clearAllParamHistory}
 */
export function clearAllParamHistory({
  preventPushHistory = false,
  isShortenUrlParam = false,
} = {}) {
  const url = new URL(window.location.href);
  const { searchParams } = url;
  const removeKeys = [];

  const validShortKeys = new Set();
  if (isShortenUrlParam) {
    for (const [longKey, shortKey] of shortParamsMap.entries()) {
      if (longKey.startsWith(FILTER_KEY_PREFIX)) {
        validShortKeys.add(shortKey);
      }
    }
  }

  searchParams.forEach((_, key) => {
    if (key.startsWith(FILTER_KEY_PREFIX)) {
      removeKeys.push(key);
    } else if (isShortenUrlParam && validShortKeys.has(key)) {
      removeKeys.push(key);
    }
  });

  removeKeys.forEach((k) => searchParams.delete(k));
  const behavior = preventPushHistory ? 'replace' : 'push';
  mutateHistory(behavior, url.href, {});
}

/**
 * @type {import('./index.d').getQueryParamByKey}
 */
export function getQueryParamByKey(key) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(key);
}

/**
 * @type {import('./index.d').getParamsHistory}
 */
export function getParamsHistory() {
  const raw = window.location.search.replace(/^\?/, '');
  const pairs = raw ? raw.split('&') : [];
  const separator = getMultipleValueSeparator();

  const rawObj = {};
  for (const p of pairs) {
    const [rawKey, rawVal = ''] = p.split('=');
    let key = decodeURIComponent(rawKey);
    if (longParamsMap.has(key)) {
      key = longParamsMap.get(key);
    }

    const escapedSeparator = separator.replace(REGEX_ESCAPE_PATTERN, '\\$&');
    const encodedSeparator = encodeURIComponent(separator);
    const escapedEncodedSeparator = encodedSeparator.replace(REGEX_ESCAPE_PATTERN, '\\$&');
    const splitRegex = new RegExp(`${escapedSeparator}|${escapedEncodedSeparator}`);

    const segments = rawVal.split(splitRegex);
    const decoded = segments.map((v) => decodeURIComponent(v.replace(/\+/g, ' ')));
    if (!rawObj[key]) rawObj[key] = [];
    rawObj[key].push(...decoded);
  }

  const paramList = [];
  for (const [k, arr] of Object.entries(rawObj)) {
    if (k.startsWith(FILTER_KEY_PREFIX)) {
      paramList.push({ key: k, values: arr });
    }
  }

  const urlParams = new URLSearchParams(window.location.search);
  const sort = urlParams.get('sort');
  const pagination = { page: urlParams.get('page') };

  return { paramList, sort, pagination };
}

/**
 * Builds a URLSearchParams object from the given params object.
 * Converts params object into a query string format by appending
 * array values and encoding values.
 * @type {import('./index.d').buildQueryParams}
 */
export function buildQueryParams(params) {
  const queryParams = new URLSearchParams();
  queryParams.sort();
  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const value = params[key];
      if (Array.isArray(value)) {
        value.forEach((item) => {
          queryParams.append(`${key}[]`, item);
        });
      } else {
        queryParams.set(key, value);
      }
    }
  }

  const urlParams = new URLSearchParams(window.location.search);
  const searchQuery = urlParams.get('q');

  if (searchQuery) {
    queryParams.set('q', searchQuery);

    if (isCollectionPage()) {
      queryParams.set('incollection_search', true);
      queryParams.set('event_type', 'incollection_search');
    }
  }

  queryParams.set('customer_id', getCustomerId());
  return queryParams;
}

/**
 * @type {import('./index.d').parseFilterTreeToParams}
 */
export const parseFilterTreeToParams = (filterTree) => {
  const filterParams = {};
  filterTree.forEach((filter) => {
    let { value } = filter.metaData || filter.data;
    const { key } = filter.metaData || filter.data;

    if (key.startsWith(`pf_st_`)) {
      value = convertValueRequestStockStatus(value);
    }

    filterParams[key] = [...(filterParams[key] || []), value];
  });
  return filterParams;
};

/**
 * @type {import('./index.d').calcPageAction}
 */
export const calcPageAction = (page, action) => {
  let newPage = Number(page);
  if (action === 'next') {
    newPage += 1;
  } else if (action === 'prev') {
    newPage = newPage - 1 > 0 ? newPage - 1 : 0;
  } else {
    newPage = Number(action);
  }

  return newPage;
};

/**
 * Check whether the filter option param exist
 * @type {import('./index.d').checkExistFilterOptionParam}
 */
export const checkExistFilterOptionParam = (isShortenUrlParam = false) => {
  const queryParams = new URLSearchParams(window.location.search);

  for (const [key] of queryParams.entries()) {
    let finalKey = key;

    if (isShortenUrlParam && longParamsMap.has(key)) {
      finalKey = longParamsMap.get(key);
    }

    if (finalKey.indexOf('pf_') === 0) {
      return true;
    }
  }

  return false;
};

/**
 * Check whether the filter option param exist
 * @type {import('./index.d').rewriteUrlFromParams}
 */
export function rewriteUrlFromParams(
  { paramList, sort, pagination },
  { isShortenUrlParam = false, urlScheme = 2 } = {}
) {
  const url = new URL(window.location.href);
  const q = url.searchParams.get('q');
  const currentCollection = url.searchParams.get('collection_scope');

  const toDelete = [];
  url.searchParams.forEach((_, key) => {
    if (key === 'sort' || key === 'page' || key === 'q' || key === 'collection_scope') return;
    if (isShortenUrlParam) {
      toDelete.push(key);
    } else {
      if (key.startsWith(FILTER_KEY_PREFIX) || longParamsMap.has(key)) {
        toDelete.push(key);
      }
    }
  });
  toDelete.forEach((k) => url.searchParams.delete(k));

  url.searchParams.delete('sort');
  url.searchParams.delete('page');
  url.searchParams.delete('q');
  url.searchParams.delete('collection_scope');
  if (sort) url.searchParams.set('sort', sort);
  if (pagination.page) url.searchParams.set('page', pagination.page);
  if (q) url.searchParams.set('q', q);
  if (currentCollection) url.searchParams.set('collection_scope', currentCollection);

  paramList.forEach(({ key: fullKey, values }) => {
    if (isShortenUrlParam && urlScheme !== 0) {
      const shortKey = shortParamsMap.get(fullKey) || fullKey;

      if (urlScheme === 1) {
        values.forEach((v) => {
          url.searchParams.append(shortKey, v);
        });
      } else {
        setShortenedParam(url, shortKey, values);
      }
    } else {
      values.forEach((v) => {
        url.searchParams.append(fullKey, v);
      });
    }
  });

  window.history.replaceState({}, '', url.href);
}
